import Button, { type ButtonProps } from '@pelckmans/elementis/components/button';
import ButtonGroup from '@pelckmans/elementis/components/button-group';
import DigibookIcon from '@pelckmans/elementis/icons/digibook';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import AccountContext from '../../../../../contexts/AccountContext';
import AppsContext from '../../../_contexts/AppsContext';
import ModuleContext from '../../../_contexts/ModuleContext';
import DigibookButton from '../../../components/DigibookButton';
import useDigibook from '../../../hooks/useDigibook';
import useSlideSets from '../../../hooks/useSlideSets';
import {
  buildCodexUrl,
  getDigibookTooltip,
  getDisabledState,
  isDigibookApp,
  isOldDigibookApp,
  type DigibookApp,
} from '../../../utils/digibookAppUtils';
import SlideSetAppButton from './SlideSetAppButton';
import WordListAppButton from './WordListAppButton';

function DigibookPageButton({
  digibookApp,
  pageNumber,
  size,
}: {
  digibookApp: DigibookApp;
  pageNumber: number;
  size: ButtonProps['size'];
}) {
  const { t } = useTranslation();
  const { account } = useContext(AccountContext);

  const disabledState = getDisabledState(digibookApp);

  const href = digibookApp.linkedApp?.id
    ? buildCodexUrl({
        digibookId: digibookApp.linkedApp?.id,
        moduleId: digibookApp.moduleId,
        accountProvider: account?.provider,
        page: pageNumber,
      })
    : '';

  return (
    <Button
      as="link"
      prominence="secondary"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      key="digibook-button"
      size={size}
      startIcon={<DigibookIcon data-testid="digibook-icon" />}
      disabled={disabledState !== 'notDisabled'}
      tooltip={disabledState !== 'notDisabled' ? t(getDigibookTooltip(disabledState)) : undefined}
    >
      {pageNumber > 0
        ? t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.OPEN_DIGIBOOK_WITH_PAGE', { page: pageNumber })
        : t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.OPEN_DIGIBOOK')}
    </Button>
  );
}

export default function AppButtonList({
  density = 'normal',
  digibooksOnly = false,
}: {
  density?: 'normal' | 'compact' | 'small';
  digibooksOnly?: boolean;
}) {
  const { module } = useContext(ModuleContext);
  const { apps } = useContext(AppsContext);

  const { t } = useTranslation();
  const { slideSets } = useSlideSets();
  const { digibook, pageNumber } = useDigibook();

  const size = density === 'compact' ? 'sm' : 'md';

  if (!module) return null;

  const digibookApp = apps.find(app => isDigibookApp(app) && app.moduleId === module?.id);
  const oldDigibookApps = apps.filter(app => isOldDigibookApp(app) && app.moduleId === module?.id);

  if (!digibook && oldDigibookApps.length === 0 && apps.length === 0 && slideSets.length === 0) return null;

  return (
    <ButtonGroup role="group" aria-label={t('PAGES.TOC_LEVEL.APPS.A11Y_LABEL')} size={size}>
      {digibookApp && (
        <DigibookPageButton digibookApp={digibookApp as DigibookApp} pageNumber={pageNumber} size={size} />
      )}
      <DigibookButton key="old-digibook" digibookApps={oldDigibookApps} size={size} />
      {!digibooksOnly && (
        <>
          <SlideSetAppButton key="presentations" size={size} />
          <WordListAppButton key="word-lists" size={size} />
        </>
      )}
    </ButtonGroup>
  );
}
